/**
 * External dependencies
 */
import clsx from 'clsx';
import { FormStep } from '@woocommerce/blocks-components';
import { CHECKOUT_STORE_KEY } from '@woocommerce/block-data';
import { registerCheckoutBlock } from '@woocommerce/blocks-checkout';
import { getSetting } from '@woocommerce/settings';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelect } from '@wordpress/data';
import { useEntityRecord } from '@wordpress/core-data';
import { store as coreStore } from '@wordpress/core-data';
import { debounce } from 'lodash';
import Fields from './fields';
/**
 * Internal dependencies
 */
import './style.scss';
import metadata from './block.json';

const cfeSettings = getSetting('thwcfe-block-integration_data', '');
const { allSections } = cfeSettings;
const { additional_info } = allSections;

const Block = ({ checkoutExtensionData }) => {

	const sectionData = additional_info;
    const { setExtensionData } = checkoutExtensionData;
    const [sectionValues, setSectionValues] = useState({});
      
    const checkoutIsProcessing = useSelect((select) =>
        select(CHECKOUT_STORE_KEY).isProcessing()
    );
    
    const moveCustomStepExecuted = useRef(false);
    /**
	 * Debounce the setExtensionData function to avoid multiple calls to the API when rapidly
	 * changing options.
	 */
    const debouncedSetExtensionData = useCallback(
        
        debounce( ( values ) => {
			setExtensionData( `thwcfe-additional-fields`, sectionData.id, values );
		}, 1000 ),
		[ setExtensionData ]
    );

    const onChangeSection = useCallback((fieldValues) => {
        setSectionValues(prevValues => ({
            ...prevValues,
            ...fieldValues
        }));
    }, []);

    useEffect(() => {
        if (Object.keys(sectionValues).length > 0) {
            debouncedSetExtensionData(sectionValues);
        }
        return () => {
            debouncedSetExtensionData.cancel();
        };
    }, [sectionValues, debouncedSetExtensionData]);

    useEffect(() => {
        if (moveCustomStepExecuted.current) return;

        const customStepId = sectionData.id;
        const stepPositionClass = `thwcfe_sections_${customStepId}`;

        const moveCustomStep = () => {

            const customStep = document.querySelector(`#${customStepId}`);
            const stepPosition = document.querySelector(`.${stepPositionClass}`);
            const checkoutActions = document.querySelector(".wc-block-checkout__actions.wp-block-woocommerce-checkout-actions-block");

            if (customStep && stepPosition && checkoutActions) {
                if (stepPosition.parentNode) {
                    stepPosition.parentNode.replaceChild(customStep, stepPosition);
                }
                moveCustomStepExecuted.current = true;
                return true;
            }
            return false;
        };

        const observer = new MutationObserver(() => {
            if (moveCustomStep()) {
                observer.disconnect();
            }
        });

        if (!moveCustomStep()) {
            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });
        }

        return () => observer.disconnect();
    }, []);

    const fields = useMemo(
        () => (Array.isArray(sectionData.fields) ? sectionData.fields : Object.values(sectionData.fields)),
        [sectionData.fields]
    );
	const Wrapper = sectionData.show_title ? FormStep : 'div';
    const hasFields = Object.keys(fields).length > 0;
    if (!hasFields) {
        return null;
    }
	return (
		<Wrapper
			id={sectionData.id}
			className={clsx('wc-block-checkout__additional-fields', sectionData.cssclass)}
			{...(sectionData.show_title && {
				title: sectionData.title,
				description: sectionData.subtitle || '',
				showStepNumber: true,
				disabled: checkoutIsProcessing,
		  	})}
		>
		  <div>
			{fields.map((field) => (
			  <div key={field.id} className="wc-block-checkout__additional-fields__field">
				<Fields
				  field={{ ...field, value: sectionValues[field.id] || '' }}
				  onChange={onChangeSection}
				/>
			  </div>
			))}
		  </div>
		</Wrapper>
	  );
	
};
export default  Block;
